import { Injectable } from '@angular/core';
import { DevicesStore, InstallationFeaturesStore, RichDevice } from '@store';
import {
  DeviceRoles,
  FeatureNames,
  HeatingRodsHeatGenerationEntityCommands,
  HeatingRodsHeatGenerationEntityProperties,
  HeatingRodsHeatGenerationProperties,
} from '@vi-iot/client/index';
import { BehaviorSubject, Observable, combineLatest, map, switchMap } from 'rxjs';
import { ElectricHeaterHelper } from './electric-heater.helper';

@Injectable({
  providedIn: 'root',
})
export class ElectricHeaterService {
  heatingRodIndexOverlay$: BehaviorSubject<string> = new BehaviorSubject(null);
  deviceId$: BehaviorSubject<string> = new BehaviorSubject(null);

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private devicesStore: DevicesStore
  ) {}

  private getMainDevice(): Observable<RichDevice> {
    const scuDevice$ = this.devicesStore
      .getDevicesByRole(DeviceRoles.SystemControllerSCU)
      .pipe(map((devices) => devices[0]));
    const selectedDevice$ = this.devicesStore.getSelectedDevice$;

    return combineLatest([scuDevice$, selectedDevice$]).pipe(
      map(([scuDevice, selectedDevice]) => scuDevice ?? selectedDevice)
    );
  }

  aggregatedHeatingRodsIndexesWithBoilerSerials(): Observable<
    ElectricHeaterHelper.HeatingRodIndexWithBoilerSerial[]
  > {
    const enabledHeatingRods$: Observable<string[]> = this.getMainDevice().pipe(
      switchMap((device) =>
        this.installationFeaturesStore
          .selectInstallationDeviceFeature<HeatingRodsHeatGenerationProperties, never>(
            FeatureNames.heatingRodsHeatGeneration,
            device.id
          )
          .pipe(map((feature) => feature?.properties?.enabled?.value))
      )
    );

    return combineLatest([enabledHeatingRods$, this.getMainDevice()]).pipe(
      switchMap(([enabledHeatingRodsIndexes, device]) => {
        const heatingRodsEntitiesFeatures = enabledHeatingRodsIndexes?.map((heatingRodIndex) =>
          this.installationFeaturesStore
            .selectInstallationDeviceFeature<
              HeatingRodsHeatGenerationEntityProperties,
              HeatingRodsHeatGenerationEntityCommands
            >(FeatureNames.heatingRodsHeatGenerationEntity(heatingRodIndex), device.id)
            .pipe(
              map(
                (feature): ElectricHeaterHelper.HeatingRodIndexWithBoilerSerial => ({
                  heatingRodIndex,
                  deviceId: feature?.deviceId,
                })
              )
            )
        );

        return combineLatest(heatingRodsEntitiesFeatures);
      })
    );
  }
}
